// Outbound webhooks (ORB-567).
//
// "Outbound" is load-bearing throughout: an unqualified "webhook" in this product means the INBOUND
// direction — the IntegrationProvider.Webhook connection that accepts form submissions from external
// systems. These types are about us calling out to a customer's endpoint.
//
// Shapes are generated from OrbitalX.Dto — see api-generated.d.ts. The status unions are narrowed
// here because the C# DTOs declare those properties `string`.

import type {
    OutboundWebhookDeliveryCountsDto,
    OutboundWebhookDeliveryViewDto as GeneratedOutboundWebhookDeliveryViewDto,
    OutboundWebhookSubscriptionUpdateDto as GeneratedOutboundWebhookSubscriptionUpdateDto,
    OutboundWebhookSubscriptionViewDto as GeneratedOutboundWebhookSubscriptionViewDto,
} from './api-generated'

export type {
    OutboundWebhookDeliveryCountsDto,
    OutboundWebhookResendAllResultDto,
    OutboundWebhookSubscriptionCreateDto,
} from './api-generated'

export type OutboundWebhookSubscriptionStatus = 'Active' | 'Paused'

export type OutboundWebhookDeliveryStatus = 'Pending' | 'Delivered' | 'Failed' | 'Cancelled'

/** `WorkspaceId` null means every workspace in the company delivers here. */
export interface OutboundWebhookSubscriptionViewDto
    extends Omit<GeneratedOutboundWebhookSubscriptionViewDto, 'Status' | 'LastDeliveryStatus'> {
    Status: OutboundWebhookSubscriptionStatus
    LastDeliveryStatus?: OutboundWebhookDeliveryStatus | null
}

/**
 * Widening a destination back to all workspaces needs `ClearWorkspaceFilter`: null is
 * indistinguishable from absent in JSON, so sending WorkspaceId: null would just be ignored.
 *
 * `ClearWorkspaceFilter` is optional here and required in the generated type, and the generated one
 * is not wrong — it is a non-nullable `bool` in C#. On a PATCH body that makes it omittable anyway:
 * an absent key deserializes to false, which is exactly "leave the filter alone". The emitter cannot
 * tell a request DTO from a response one, so that distinction has to live here.
 */
export interface OutboundWebhookSubscriptionUpdateDto
    extends Omit<GeneratedOutboundWebhookSubscriptionUpdateDto, 'Status' | 'ClearWorkspaceFilter'> {
    Status?: OutboundWebhookSubscriptionStatus | null
    ClearWorkspaceFilter?: boolean
}

/**
 * Returned ONLY from create and rotate. The secret is encrypted at rest and decrypted solely to sign
 * an outgoing request, so there is no read path that recovers it. The UI gets one chance to show it.
 */
export interface OutboundWebhookSecretRevealDto {
    Subscription: OutboundWebhookSubscriptionViewDto
    Secret: string
}

/**
 * `Attempts` counts contacts actually made — it reads 1 after one attempt, never 0. `RedactedUrl` is
 * the destination with credentials, query and fragment stripped. `ResentToId` is set when this row
 * has been superseded by a resend (the UI hides its Resend button); `ResentFromId` when it IS one.
 */
export interface OutboundWebhookDeliveryViewDto
    extends Omit<GeneratedOutboundWebhookDeliveryViewDto, 'Status'> {
    Status: OutboundWebhookDeliveryStatus
}

/** The deliveries endpoint projects an anonymous object, so there is no DTO to generate from. */
export interface OutboundWebhookDeliveryListResponse {
    Deliveries: OutboundWebhookDeliveryViewDto[]
    Counts: OutboundWebhookDeliveryCountsDto
}

/** Also an anonymous object — the test endpoint reports its own result inline. */
export interface OutboundWebhookTestResultDto {
    Delivered: boolean
    StatusCode?: number | null
    Error?: string | null
    DeliveryId?: string | null
}

/** Why a resend was refused. Mirrors the server's enum; drives which message we show. */
export type OutboundWebhookResendRefusal = 'NotFound' | 'StillInProgress' | 'AlreadyResent'

export interface OutboundWebhookResendRefusalDto {
    Reason: OutboundWebhookResendRefusal
    Message: string
}

/**
 * Status presentation.
 *
 * The pill classes deliberately mirror INTEGRATION_STATUS_COLORS in `types/integrations.ts` step for
 * step. Delivered reads exactly like a connection's Active, Failed exactly like its Error. These
 * badges sit on the same settings screen, so two different green pills a scroll apart would be one
 * vocabulary too many.
 *
 * Note what this means about the brand: `--color-green-500` IS the brand green (#52de45), but every
 * other step in the ramp is stock. The existing status scale uses 100/800 in light and 900/30 + 300
 * in dark, so it never touches 500 — green can safely mean "good" here without colliding with the
 * green primary buttons, which is a question this app already answered before webhooks existed.
 *
 * Colour is never the only signal regardless: every status also carries a glyph and a label, which
 * the accessibility floor in PRODUCT.md requires.
 */
export const DELIVERY_STATUS_PRESENTATION: Record<OutboundWebhookDeliveryStatus, {
    label: string
    glyph: string
    className: string
    /** Solid fill for the rail sparkline. Stock ramp steps, never the brand 500. */
    dotClassName: string
}> = {
    Delivered: {
        label: 'Delivered',
        glyph: '✓',
        className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
        dotClassName: 'bg-green-600 dark:bg-green-400',
    },
    Pending: {
        label: 'Pending',
        glyph: '↻',
        className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
        dotClassName: 'bg-amber-500 dark:bg-amber-400',
    },
    Failed: {
        label: 'Failed',
        glyph: '✕',
        className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
        dotClassName: 'bg-red-600 dark:bg-red-400',
    },
    Cancelled: {
        label: 'Cancelled',
        glyph: '⊘',
        className: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
        dotClassName: 'bg-slate-400 dark:bg-slate-500',
    },
}
